import { useEffect, useState } from "react";
import { useAutoOpen } from "@/hooks/useAutoOpen";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Boxes, Pencil, Trash2, PackageCheck, Sun } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { CetakBriketDialog } from "@/components/app/bakar/CetakBriketDialog";
import { SelesaiBriketDialog } from "@/components/app/bakar/SelesaiBriketDialog";
import { StokBahanCards } from "@/pages/BahanBriket";

const kgFmt = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} Kg`;
const sisaLabel = (n) => n === null || n === undefined ? "" : n > 0 ? `${n} hari lagi` : "Siap diangkat";

const AktifCard = ({ b, onSelesai, onEdit, onDelete }) => {
  const siap = (b.sisa_hari ?? 1) <= 0;
  return (
    <div className="card-flat p-4 space-y-3" data-testid={`briket-aktif-${b.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Sun size={15} className="text-violet-600"/>{b.produk_nama}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Cetak {tanggalID(b.tanggal_mulai)} · Selesai ±{tanggalID(b.estimasi_selesai)}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onEdit(b)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-briket-${b.id}`}><Pencil size={14}/></button>
          <button onClick={() => onDelete(b)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-briket-${b.id}`}><Trash2 size={14} className="text-rose-600"/></button>
        </div>
      </div>
      <div className={`text-xs font-medium ${siap ? "text-emerald-700" : "text-violet-700"}`} data-testid={`briket-sisa-${b.id}`}>{sisaLabel(b.sisa_hari)}</div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><div className="text-slate-500">Bahan Stok</div><div className="font-mono font-semibold">{kgFmt(b.bahan_stok_kg)}</div></div>
        <div><div className="text-slate-500">Biaya s.d. Cetak</div><div className="font-mono font-semibold">{rupiah(b.total_biaya)}</div></div>
      </div>
      <Button onClick={() => onSelesai(b)} className={`w-full rounded-sm ${siap ? "brand-btn" : ""}`} variant={siap ? "default" : "outline"} data-testid={`selesai-btn-${b.id}`}>Selesai & Catat Hasil</Button>
    </div>
  );
};

export default function ProduksiBriket() {
  const [items, setItems] = useState([]);
  const [stokBahan, setStokBahan] = useState([]);
  const [produkList, setProdukList] = useState([]);
  const [cetakOpen, setCetakOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selesaiItem, setSelesaiItem] = useState(null);

  const load = async () => {
    try {
      const [a, b, k] = await Promise.all([api.get("/batch-briket"), api.get("/stok-bahan-briket"), api.get("/kategori", { params: { tipe: "produk" } })]);
      setItems(a.data); setStokBahan(b.data); setProdukList(k.data.filter((p) => p.sumber === "briket"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (b) => { setEditItem(b); setCetakOpen(true); };
  useAutoOpen(() => { setEditItem(null); setCetakOpen(true); });
  const del = async (b) => {
    if (!window.confirm("Hapus batch briket ini? Bahan kembali ke stok, produksi & pengeluaran terkait ikut dihapus.")) return;
    try { await api.delete(`/batch-briket/${b.id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const aktif = items.filter((b) => b.status === "dijemur");
  const selesai = items.filter((b) => b.status === "selesai");
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(selesai, 10);

  return (<>
    <PageHeader title="Produksi Briket" subtitle="Cetak briket dari stok bahan (kiriman pabrik lain / beli) + bahan tambahan → jemur/oven → selesai & masuk stok."
      actions={<Button onClick={() => { setEditItem(null); setCetakOpen(true); }} className="brand-btn rounded-sm" data-testid="cetak-briket-btn"><Boxes size={14} className="mr-1"/>Cetak Briket</Button>}/>
    <div className="p-4 md:p-8 space-y-6">
      <section className="space-y-3" data-testid="stok-bahan-section">
        <h3 className="text-sm md:text-base font-semibold text-slate-900">Stok Bahan Briket</h3>
        <StokBahanCards items={stokBahan}/>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm md:text-base font-semibold text-slate-900">Sedang Dijemur/Oven ({aktif.length})</h3>
        {aktif.length === 0
          ? <div className="card-flat p-6 text-center text-sm text-slate-400" data-testid="briket-aktif-empty">Tidak ada briket yang sedang dijemur/oven.</div>
          : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
              {aktif.map((b) => <AktifCard key={b.id} b={b} onSelesai={setSelesaiItem} onEdit={openEdit} onDelete={del}/>)}
            </div>}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm md:text-base font-semibold text-slate-900">Riwayat Briket Selesai</h3>
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs md:text-sm" data-testid="briket-selesai-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="p-3">Cetak</th><th className="p-3">Selesai</th><th className="p-3">Produk</th><th className="p-3 text-right">Bahan Stok</th>
                  <th className="p-3 text-right">Biaya Bahan Stok</th><th className="p-3 text-right">Bahan Luar</th><th className="p-3 text-right">Operasional</th>
                  <th className="p-3 text-right">Hasil</th><th className="p-3 text-right">HPP/Kg</th><th className="p-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {selesai.length === 0 && <tr><td colSpan="10" className="text-center py-10 text-slate-400">Belum ada briket selesai.</td></tr>}
                {paged.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`briket-row-${b.id}`}>
                    <td className="p-3 whitespace-nowrap">{tanggalID(b.tanggal_mulai)}</td>
                    <td className="p-3 whitespace-nowrap">{tanggalID(b.tanggal_selesai)}</td>
                    <td className="p-3"><span className="text-[11px] px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 whitespace-nowrap">{b.produk_nama}</span></td>
                    <td className="p-3 text-right font-mono" title={(b.bahan_stok || []).map((r) => `${r.nama}: ${kgFmt(r.kg)}`).join(", ")}>{kgFmt(b.bahan_stok_kg)}</td>
                    <td className="p-3 text-right font-mono">{rupiah(b.biaya_bahan_stok)}</td>
                    <td className="p-3 text-right font-mono" title={(b.bahan_luar || []).map((r) => `${r.nama}: ${rupiah(r.biaya)}`).join(", ")}>{rupiah(b.biaya_bahan_luar)}</td>
                    <td className="p-3 text-right font-mono">{rupiah(Number(b.biaya_operasional || 0) + Number(b.biaya_tambahan || 0))}</td>
                    <td className="p-3 text-right font-mono font-semibold">{kgFmt(b.output_kg)}</td>
                    <td className="p-3 text-right font-mono brand-text font-semibold" data-testid={`briket-hpp-${b.id}`}>{rupiah(b.hpp_per_kg)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setSelesaiItem(b)} title="Ubah hasil" className="p-1.5 rounded-sm hover:bg-emerald-50" data-testid={`edit-selesai-${b.id}`}><PackageCheck size={14} className="text-emerald-600"/></button>
                        <button onClick={() => openEdit(b)} title="Ubah data cetak" className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-briket-${b.id}`}><Pencil size={14}/></button>
                        <button onClick={() => del(b)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-briket-${b.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="briket-pagination"/>
        </div>
      </section>
    </div>

    <CetakBriketDialog open={cetakOpen} setOpen={setCetakOpen} item={editItem} stokBahan={stokBahan} produkList={produkList} onSaved={load}/>
    <SelesaiBriketDialog open={!!selesaiItem} setOpen={(v) => !v && setSelesaiItem(null)} item={selesaiItem} onSaved={load}/>
  </>);
}
